const translations = {
    en: {
      welcome: {
        title: "Welcome!",
        description: "Not sure which UX research method to choose? Don't worry. We're here to help you find the perfect balance between qualitative and quantitative methods. Just answer a few questions, and we'll guide you to the best option for your project. It's that simple! ✨",
        button: "Get Started 🚀"
      },
      qualitative: {
        title: "Exploration and Deepening",
        questions: [
          { question: "Do you need to know why users struggle?", example: "Example: If users abandon a task halfway, it's important to understand why." },
          { question: "Do you want to uncover hidden motivations?", example: "Example: Understanding why users prefer one option over another can reveal underlying motivations." },
          { question: "Do you need detailed feedback on a prototype?", example: "Example: Before launching a product, knowing users' detailed opinions on the design is crucial." },
          { question: "Do you want to explore users' feelings about a design?", example: "Example: Finding out if users find the design intuitive or frustrating." },
          { question: "Do you need to hear what users think during a task?", example: "Example: Knowing how users think out loud while completing a task can reveal unexpected issues." },
        ],
        button: "Measurement and Comparison"
      },
      quantitative: {
        title: "Measurement and Comparison",
        questions: [
          { question: "Do you need to calculate feature success rates?", example: "Example: Measuring how many users complete a task without errors." },
          { question: "Do you want to test which design works better?", example: "Example: Comparing two versions of a page to see which has a higher conversion rate." },
          { question: "Do you need to measure user satisfaction on a scale?", example: "Example: Asking users to rate their experience on a scale of 1 to 10." },
          { question: "Do you want to compare behavior across groups?", example: "Example: Comparing how users of different ages interact with your app." },
          { question: "Do you need to measure how often users succeed?", example: "Example: Evaluating how many times users successfully complete a task versus how often they fail." },
        ],
        button: "See Results"
      },
      result: {
        title: "Final Result",
        recommendation: "Recommended Research Method:",
        testsTitle: "Test Recommendations",
        toolsTitle: "Suggested Tools",
        descriptions: {
          qualitative: "The qualitative method allows for a deep understanding of users' experiences and motivations.",
          quantitative: "The quantitative method is ideal for measuring and analyzing data statistically.",
          mixed: "The mixed approach combines qualitative and quantitative methods for a complete view."
        },
        tests: {
          qualitative: [
            { name: 'In-depth Interviews', detail: 'Explore users’ opinions and feelings through open-ended questions.' },
            { name: 'Contextual Observation', detail: 'Study users in their environment to understand how they interact with the product.' },
            { name: 'Focus Groups', detail: 'Discuss topics with groups to gain multiple perspectives on an issue.' },
            { name: 'User Diaries', detail: 'Allow users to document their experiences with the product over time.' },
            { name: 'Empathy Maps', detail: 'Help understand users’ thoughts and emotions while interacting with the product.' },
            { name: 'Personas', detail: 'Create representative user profiles to guide design.' },
            { name: 'Experience Mapping', detail: 'Visualize the user journey across different stages of product use.' }
          ],
          quantitative: [
            { name: 'Surveys', detail: 'Collect structured data from a large number of users for statistical analysis.' },
            { name: 'A/B Testing', detail: 'Compare two versions of a page or feature to see which performs better.' },
            { name: 'Tree Testing', detail: 'Evaluate the navigation structure to ensure users can easily find information.' },
            { name: 'Analytics', detail: 'Monitor and analyze user behavior through metrics such as time on page or conversion rates.' },
            { name: 'Benchmarking', detail: 'Compare your product’s performance against competitors.' },
            { name: 'Heuristic Evaluation', detail: 'Identify usability issues through expert review.' },
            { name: 'Kano Model', detail: 'Classify product features based on their impact on user satisfaction.' }
          ],
          mixed: [
            { name: 'Case Studies', detail: 'Analyze real-world situations combining qualitative and quantitative data.' },
            { name: 'Participatory Design Research', detail: 'Involve users in the design process to co-create effective solutions.' },
            { name: 'Experience Mapping', detail: 'Combine observations and data to visualize the user journey across the product.' },
            { name: 'Card Sorting', detail: 'Help structure content in a way that makes sense to users.' },
            { name: 'Focus Groups', detail: 'Discuss topics with groups to gain multiple perspectives on an issue.' },
            { name: 'A/B Testing', detail: 'Compare two versions of a page or feature to see which performs better.' }
          ]
        },
        tools: "Suggested Tools:",
        toolsList: {
          qualitative: ['Lookback', 'UserTesting', 'Optimal Workshop', 'Dovetail', 'VWO', 'Amplitude', 'Datadog'],
          quantitative: ['Google Analytics', 'Hotjar', 'SurveyMonkey', 'Mixpanel', 'VWO', 'Amplitude', 'Datadog'],
          mixed: ['Maze', 'Qualtrics', 'UsabilityHub', 'FullStory', 'Google Analytics', 'Hotjar', 'SurveyMonkey', 'Mixpanel', 'VWO', 'Amplitude', 'Datadog']
        },
        restart: "Start Over",
        note: "Note: We have no commercial ties to the suggested tools.",
        share: "Did you like the tool? Share it on social media!"
      },
      footer: {
        copyright: "©La Mala 2024",
        buyMeACoffee: "Buy me a coffee"
      }
    },
    es: {
      welcome: {
        title: "¡Bienvenido!",
        description: "¿No sabes qué método de investigación UX elegir? No te preocupes. Estamos aquí para ayudarte a encontrar el equilibrio perfecto entre métodos cualitativos y cuantitativos. Responde algunas preguntas, y te guiaremos hacia la mejor opción para tu proyecto. ¡Así de sencillo! ✨",
        button: "Comenzar 🚀"
      },
      qualitative: {
        title: "Exploración y Profundización",
        questions: [
          { question: "¿Necesitas saber por qué los usuarios tienen dificultades?", example: "Ejemplo: Si los usuarios abandonan una tarea a mitad de camino, es importante entender el porqué." },
          { question: "¿Quieres descubrir motivaciones ocultas?", example: "Ejemplo: Entender por qué los usuarios prefieren una opción sobre otra puede revelar motivaciones subyacentes." },
          { question: "¿Necesitas retroalimentación detallada sobre un prototipo?", example: "Ejemplo: Antes de lanzar un producto, conocer la opinión detallada de los usuarios sobre el diseño es crucial." },
          { question: "¿Quieres explorar los sentimientos de los usuarios sobre un diseño?", example: "Ejemplo: Averiguar si los usuarios encuentran el diseño intuitivo o frustrante." },
          { question: "¿Necesitas escuchar lo que piensan los usuarios durante una tarea?", example: "Ejemplo: Saber cómo los usuarios piensan en voz alta mientras completan una tarea puede revelar problemas no anticipados." },
        ],
        button: "Medición y Comparación"
      },
      quantitative: {
        title: "Medición y Comparación",
        questions: [
          { question: "¿Necesitas calcular las tasas de éxito de una función?", example: "Ejemplo: Medir cuántos usuarios completan una tarea sin errores." },
          { question: "¿Quieres probar cuál diseño funciona mejor?", example: "Ejemplo: Comparar dos versiones de una página para ver cuál tiene una tasa de conversión más alta." },
          { question: "¿Necesitas medir la satisfacción del usuario en una escala?", example: "Ejemplo: Pedir a los usuarios que califiquen su experiencia en una escala del 1 al 10." },
          { question: "¿Quieres comparar el comportamiento entre grupos?", example: "Ejemplo: Comparar cómo usuarios de diferentes edades interactúan con tu aplicación." },
          { question: "¿Necesitas medir cuán a menudo los usuarios tienen éxito?", example: "Ejemplo: Evaluar cuántas veces los usuarios completan una tarea con éxito frente a las veces que fallan." },
        ],
        button: "Ver Resultados"
      },
      result: {
        title: "Resultado Final",
        recommendation: "Método de Investigación Recomendado:",
        testsTitle: "Recomendaciones de Pruebas",
        toolsTitle: "Herramientas Sugeridas",
        descriptions: {
          qualitative: "El método cualitativo permite una comprensión profunda de las experiencias y motivaciones del usuario.",
          quantitative: "El método cuantitativo es ideal para medir y analizar datos de manera estadística.",
          mixed: "El enfoque mixto combina métodos cualitativos y cuantitativos para una visión completa."
        },
        tests: {
          qualitative: [
            { name: 'Entrevistas en profundidad', detail: 'Explora las opiniones y sentimientos del usuario a través de preguntas abiertas.' },
            { name: 'Observación contextual', detail: 'Estudia a los usuarios en su entorno para comprender cómo interactúan con el producto.' },
            { name: 'Focus Groups', detail: 'Discute temas con grupos para obtener múltiples perspectivas sobre un tema.' },
            { name: 'Diarios de usuario', detail: 'Permite a los usuarios registrar sus experiencias con el producto a lo largo del tiempo.' },
            { name: 'Mapas de empatía', detail: 'Ayuda a entender los pensamientos y emociones de los usuarios al interactuar con el producto.' },
            { name: 'Personas', detail: 'Crea perfiles de usuarios representativos para guiar el diseño.' },
            { name: 'Mapeo de experiencia', detail: 'Visualiza el recorrido del usuario a través de diferentes etapas del uso del producto.' }
          ],
          quantitative: [
            { name: 'Cuestionarios', detail: 'Recoge datos estructurados de una gran cantidad de usuarios para análisis estadístico.' },
            { name: 'Pruebas A/B', detail: 'Compara dos versiones de una página o característica para ver cuál tiene mejor rendimiento.' },
            { name: 'Tree Testing', detail: 'Evalúa la estructura de navegación para asegurar que los usuarios encuentran fácilmente la información.' },
            { name: 'Analíticas', detail: 'Monitorea y analiza el comportamiento del usuario mediante métricas como el tiempo en página o tasas de conversión.' },
            { name: 'Benchmarking', detail: 'Compara el rendimiento de tu producto con el de la competencia.' },
            { name: 'Evaluación heurística', detail: 'Identifica problemas de usabilidad a través de la revisión de expertos.' },
            { name: 'Modelo Kano', detail: 'Clasifica características del producto según su impacto en la satisfacción del usuario.' }
          ],
          mixed: [
            { name: 'Estudios de casos', detail: 'Analiza situaciones reales combinando datos cualitativos y cuantitativos.' },
            { name: 'Investigación de diseño participativo', detail: 'Involucra a los usuarios en el proceso de diseño para co-crear soluciones efectivas.' },
            { name: 'Mapeo de experiencia', detail: 'Combina observaciones y datos para visualizar el recorrido del usuario a través del producto.' },
            { name: 'Test de Card Sorting', detail: 'Ayuda a estructurar el contenido de manera intuitiva para los usuarios.' },
            { name: 'Focus Groups', detail: 'Discute temas con grupos para obtener múltiples perspectivas sobre un tema.' },
            { name: 'Pruebas A/B', detail: 'Compara dos versiones de una página o característica para ver cuál tiene mejor rendimiento.' }
          ]
        },
        tools: "Herramientas Sugeridas:",
        toolsList: {
          qualitative: ['Lookback', 'UserTesting', 'Optimal Workshop', 'Dovetail', 'VWO', 'Amplitude', 'Datadog'],
          quantitative: ['Google Analytics', 'Hotjar', 'SurveyMonkey', 'Mixpanel', 'VWO', 'Amplitude', 'Datadog'],
          mixed: ['Maze', 'Qualtrics', 'UsabilityHub', 'FullStory', 'Google Analytics', 'Hotjar', 'SurveyMonkey', 'Mixpanel', 'VWO', 'Amplitude', 'Datadog']
        },
        restart: "Volver a Empezar",
        note: "Nota: No tenemos ningún vínculo comercial con las herramientas sugeridas.",
        share: "¿Te ha gustado la herramienta? ¡Compártela en redes!"
      },
      footer: {
        copyright: "©La Mala 2024",
        buyMeACoffee: "Buy me a coffee"
      }
    },
    fr: {
  welcome: {
    title: "Bienvenue !",
    description: "Vous ne savez pas quel méthode de recherche UX choisir ? Ne vous inquiétez pas. Nous sommes là pour vous aider à trouver le parfait équilibre entre les méthodes qualitatives et quantitatives. Répondez simplement à quelques questions, et nous vous guiderons vers la meilleure option pour votre projet. C'est aussi simple que ça ! ✨",
    button: "Commencer 🚀"
  },
  qualitative: {
    title: "Exploration et Approfondissement",
    questions: [
      { question: "Avez-vous besoin de savoir pourquoi les utilisateurs rencontrent des difficultés ?", example: "Exemple : Si les utilisateurs abandonnent une tâche à mi-chemin, il est important de comprendre pourquoi." },
      { question: "Voulez-vous découvrir des motivations cachées ?", example: "Exemple : Comprendre pourquoi les utilisateurs préfèrent une option à une autre peut révéler des motivations sous-jacentes." },
      { question: "Avez-vous besoin de retours détaillés sur un prototype ?", example: "Exemple : Avant de lancer un produit, il est crucial de connaître les opinions détaillées des utilisateurs sur le design." },
      { question: "Souhaitez-vous explorer les sentiments des utilisateurs à propos d'un design ?", example: "Exemple : Découvrir si les utilisateurs trouvent le design intuitif ou frustrant." },
      { question: "Avez-vous besoin d'entendre ce que pensent les utilisateurs pendant une tâche ?", example: "Exemple : Savoir comment les utilisateurs pensent à voix haute en accomplissant une tâche peut révéler des problèmes inattendus." },
    ],
    button: "Mesure et Comparaison"
  },
  quantitative: {
    title: "Mesure et Comparaison",
    questions: [
      { question: "Avez-vous besoin de calculer les taux de réussite des fonctionnalités ?", example: "Exemple : Mesurer combien d'utilisateurs accomplissent une tâche sans erreurs." },
      { question: "Voulez-vous tester quel design fonctionne le mieux ?", example: "Exemple : Comparer deux versions d'une page pour voir laquelle a un taux de conversion plus élevé." },
      { question: "Avez-vous besoin de mesurer la satisfaction des utilisateurs sur une échelle ?", example: "Exemple : Demander aux utilisateurs de noter leur expérience sur une échelle de 1 à 10." },
      { question: "Voulez-vous comparer le comportement à travers des groupes ?", example: "Exemple : Comparer comment les utilisateurs de différents âges interagissent avec votre application." },
      { question: "Avez-vous besoin de mesurer la fréquence à laquelle les utilisateurs réussissent ?", example: "Exemple : Évaluer combien de fois les utilisateurs accomplissent une tâche avec succès par rapport au nombre d'échecs." },
    ],
    button: "Voir les Résultats"
  },
  result: {
    title: "Résultat Final",
    recommendation: "Méthode de Recherche Recommandée :",
    testsTitle: "Recommandations de Tests",
    toolsTitle: "Outils Suggérés",
    descriptions: {
      qualitative: "La méthode qualitative permet une compréhension profonde des expériences et des motivations des utilisateurs.",
      quantitative: "La méthode quantitative est idéale pour mesurer et analyser les données de manière statistique.",
      mixed: "L'approche mixte combine des méthodes qualitatives et quantitatives pour une vue d'ensemble complète."
    },
    tests: {
      qualitative: [
        { name: "Interviews approfondies", detail: "Explorer les opinions et les sentiments des utilisateurs grâce à des questions ouvertes." },
        { name: "Observation contextuelle", detail: "Étudier les utilisateurs dans leur environnement pour comprendre comment ils interagissent avec le produit." },
        { name: "Groupes de discussion", detail: "Discuter de sujets avec des groupes pour obtenir plusieurs points de vue sur une question." },
        { name: "Journaux d'utilisateurs", detail: "Permettre aux utilisateurs de documenter leurs expériences avec le produit au fil du temps." },
        { name: "Cartes d'empathie", detail: "Aider à comprendre les pensées et émotions des utilisateurs lors de l'interaction avec le produit." },
        { name: "Personas", detail: "Créer des profils d'utilisateurs représentatifs pour guider la conception." },
        { name: "Cartographie de l'expérience", detail: "Visualiser le parcours de l'utilisateur à travers les différentes étapes d'utilisation du produit." }
      ],
      quantitative: [
        { name: "Enquêtes", detail: "Collecter des données structurées auprès d'un grand nombre d'utilisateurs pour une analyse statistique." },
        { name: "Tests A/B", detail: "Comparer deux versions d'une page ou d'une fonctionnalité pour voir laquelle fonctionne mieux." },
        { name: "Test d'Arborescence", detail: "Évaluer la structure de navigation pour s'assurer que les utilisateurs trouvent facilement les informations." },
        { name: "Analyses", detail: "Surveiller et analyser le comportement des utilisateurs à travers des métriques telles que le temps passé sur la page ou les taux de conversion." },
        { name: "Étalonnage", detail: "Comparer les performances de votre produit avec celles des concurrents." },
        { name: "Évaluation heuristique", detail: "Identifier les problèmes d'utilisabilité à travers une évaluation par des experts." },
        { name: "Modèle Kano", detail: "Classer les fonctionnalités du produit en fonction de leur impact sur la satisfaction des utilisateurs." }
      ],
      mixed: [
        { name: "Études de cas", detail: "Analyser des situations réelles en combinant des données qualitatives et quantitatives." },
        { name: "Recherche participative en design", detail: "Impliquer les utilisateurs dans le processus de conception pour co-créer des solutions efficaces." },
        { name: "Cartographie de l'expérience", detail: "Combiner les observations et les données pour visualiser le parcours de l'utilisateur à travers le produit." },
        { name: "Tri de cartes", detail: "Aider à structurer le contenu d'une manière qui a du sens pour les utilisateurs." },
        { name: "Groupes de discussion", detail: "Discuter de sujets avec des groupes pour obtenir plusieurs points de vue sur une question." },
        { name: "Tests A/B", detail: "Comparer deux versions d'une page ou d'une fonctionnalité pour voir laquelle fonctionne mieux." }
      ]
    },
    tools: "Outils Suggérés :",
    toolsList: {
      qualitative: ["Lookback", "UserTesting", "Optimal Workshop", "Dovetail", "VWO", "Amplitude", "Datadog"],
      quantitative: ["Google Analytics", "Hotjar", "SurveyMonkey", "Mixpanel", "VWO", "Amplitude", "Datadog"],
      mixed: ["Maze", "Qualtrics", "UsabilityHub", "FullStory", "Google Analytics", "Hotjar", "SurveyMonkey", "Mixpanel", "VWO", "Amplitude", "Datadog"]
    },
    restart: "Recommencer",
    note: "Remarque : Nous n'avons aucun lien commercial avec les outils suggérés.",
    share: "Vous avez aimé l'outil ? Partagez-le sur les réseaux sociaux !"
  },
  footer: {
    copyright: "©La Mala 2024",
    buyMeACoffee: "Offrez-moi un café"
  }
},
de: {
  welcome: {
    title: "Willkommen!",
    description: "Sind Sie sich unsicher, welche UX-Forschungsmethode Sie wählen sollen? Keine Sorge. Wir helfen Ihnen, das perfekte Gleichgewicht zwischen qualitativen und quantitativen Methoden zu finden. Beantworten Sie einfach ein paar Fragen, und wir führen Sie zur besten Option für Ihr Projekt. So einfach ist das! ✨",
    button: "Loslegen 🚀"
  },
  qualitative: {
    title: "Erkundung und Vertiefung",
    questions: [
      { question: "Müssen Sie wissen, warum Benutzer Schwierigkeiten haben?", example: "Beispiel: Wenn Benutzer eine Aufgabe vorzeitig abbrechen, ist es wichtig zu verstehen, warum." },
      { question: "Möchten Sie verborgene Motive aufdecken?", example: "Beispiel: Zu verstehen, warum Benutzer eine Option bevorzugen, kann zugrunde liegende Motive aufzeigen." },
      { question: "Brauchen Sie detailliertes Feedback zu einem Prototyp?", example: "Beispiel: Bevor Sie ein Produkt auf den Markt bringen, ist es entscheidend, die detaillierten Meinungen der Benutzer zum Design zu kennen." },
      { question: "Möchten Sie die Gefühle der Benutzer gegenüber einem Design erkunden?", example: "Beispiel: Herausfinden, ob Benutzer das Design intuitiv oder frustrierend finden." },
      { question: "Müssen Sie hören, was Benutzer während einer Aufgabe denken?", example: "Beispiel: Zu wissen, wie Benutzer laut denken, während sie eine Aufgabe erledigen, kann unerwartete Probleme aufdecken." },
    ],
    button: "Messung und Vergleich"
  },
  quantitative: {
    title: "Messung und Vergleich",
    questions: [
      { question: "Müssen Sie die Erfolgsrate einer Funktion berechnen?", example: "Beispiel: Messen, wie viele Benutzer eine Aufgabe fehlerfrei abschließen." },
      { question: "Möchten Sie testen, welches Design besser funktioniert?", example: "Beispiel: Vergleichen Sie zwei Versionen einer Seite, um zu sehen, welche eine höhere Konversionsrate hat." },
      { question: "Müssen Sie die Benutzerzufriedenheit auf einer Skala messen?", example: "Beispiel: Benutzer bitten, ihre Erfahrung auf einer Skala von 1 bis 10 zu bewerten." },
      { question: "Möchten Sie das Verhalten zwischen Gruppen vergleichen?", example: "Beispiel: Vergleich, wie Benutzer unterschiedlichen Alters mit Ihrer App interagieren." },
      { question: "Müssen Sie messen, wie oft Benutzer erfolgreich sind?", example: "Beispiel: Evaluieren, wie oft Benutzer eine Aufgabe erfolgreich abschließen und wie oft sie scheitern." },
    ],
    button: "Ergebnisse anzeigen"
  },
  result: {
    title: "Endergebnis",
    recommendation: "Empfohlene Forschungsmethode:",
    testsTitle: "Testempfehlungen",
    toolsTitle: "Vorgeschlagene Tools",
    descriptions: {
      qualitative: "Die qualitative Methode ermöglicht ein tiefes Verständnis der Benutzererfahrungen und -motive.",
      quantitative: "Die quantitative Methode eignet sich ideal zum Messen und Analysieren von Daten auf statistischer Basis.",
      mixed: "Der gemischte Ansatz kombiniert qualitative und quantitative Methoden für eine umfassende Sicht."
    },
    tests: {
      qualitative: [
        { name: "Tiefeninterviews", detail: "Erkunden Sie die Meinungen und Gefühle der Benutzer durch offene Fragen." },
        { name: "Kontextuelle Beobachtung", detail: "Untersuchen Sie Benutzer in ihrer Umgebung, um zu verstehen, wie sie mit dem Produkt interagieren." },
        { name: "Fokusgruppen", detail: "Diskutieren Sie Themen in Gruppen, um mehrere Perspektiven zu einem Thema zu erhalten." },
        { name: "Benutzertagebücher", detail: "Lassen Sie Benutzer ihre Erfahrungen mit dem Produkt über die Zeit dokumentieren." },
        { name: "Empathiekarten", detail: "Helfen Sie dabei, die Gedanken und Emotionen der Benutzer während der Interaktion mit dem Produkt zu verstehen." },
        { name: "Personas", detail: "Erstellen Sie repräsentative Benutzerprofile, um das Design zu leiten." },
        { name: "Erlebnis-Mapping", detail: "Visualisieren Sie die Benutzerreise durch verschiedene Phasen der Produktnutzung." }
      ],
      quantitative: [
        { name: "Umfragen", detail: "Sammeln Sie strukturierte Daten von einer großen Anzahl von Benutzern zur statistischen Analyse." },
        { name: "A/B-Tests", detail: "Vergleichen Sie zwei Versionen einer Seite oder Funktion, um zu sehen, welche besser abschneidet." },
        { name: "Baumtests", detail: "Bewerten Sie die Navigationsstruktur, um sicherzustellen, dass Benutzer Informationen leicht finden können." },
        { name: "Analytik", detail: "Überwachen und analysieren Sie das Benutzerverhalten anhand von Metriken wie Verweildauer auf der Seite oder Konversionsraten." },
        { name: "Benchmarking", detail: "Vergleichen Sie die Leistung Ihres Produkts mit der Konkurrenz." },
        { name: "Heuristische Evaluation", detail: "Identifizieren Sie Usability-Probleme durch Expertenbewertungen." },
        { name: "Kano-Modell", detail: "Klassifizieren Sie Produktmerkmale nach ihrem Einfluss auf die Benutzerzufriedenheit." }
      ],
      mixed: [
        { name: "Fallstudien", detail: "Analysieren Sie reale Situationen, indem Sie qualitative und quantitative Daten kombinieren." },
        { name: "Partizipative Designforschung", detail: "Beteiligen Sie Benutzer am Designprozess, um effektive Lösungen gemeinsam zu entwickeln." },
        { name: "Erlebnis-Mapping", detail: "Kombinieren Sie Beobachtungen und Daten, um die Benutzerreise durch das Produkt zu visualisieren." },
        { name: "Kartensortierung", detail: "Helfen Sie dabei, Inhalte auf eine Weise zu strukturieren, die für Benutzer sinnvoll ist." },
        { name: "Fokusgruppen", detail: "Diskutieren Sie Themen in Gruppen, um mehrere Perspektiven zu einem Thema zu erhalten." },
        { name: "A/B-Tests", detail: "Vergleichen Sie zwei Versionen einer Seite oder Funktion, um zu sehen, welche besser abschneidet." }
      ]
    },
    tools: "Vorgeschlagene Tools:",
    toolsList: {
      qualitative: ["Lookback", "UserTesting", "Optimal Workshop", "Dovetail", "VWO", "Amplitude", "Datadog"],
      quantitative: ["Google Analytics", "Hotjar", "SurveyMonkey", "Mixpanel", "VWO", "Amplitude", "Datadog"],
      mixed: ["Maze", "Qualtrics", "UsabilityHub", "FullStory", "Google Analytics", "Hotjar", "SurveyMonkey", "Mixpanel", "VWO", "Amplitude", "Datadog"]
    },
    restart: "Neu Starten",
    note: "Hinweis: Wir haben keine kommerziellen Verbindungen zu den vorgeschlagenen Tools.",
    share: "Hat Ihnen das Tool gefallen? Teilen Sie es in den sozialen Medien!"
  },
  footer: {
    copyright: "©La Mala 2024",
    buyMeACoffee: "Kauf mir einen Kaffee"
  }
},
    zh: {
      welcome: {
        title: "欢迎！",
        description: "不确定选择哪种UX研究方法？别担心。我们将在定性和定量方法之间帮助您找到完美的平衡。只需回答几个问题，我们就会为您的项目推荐最佳选项。就是这么简单！✨",
        button: "开始 🚀"
      },
      qualitative: {
        title: "探索与深入",
        questions: [
          { question: "您是否需要了解用户为何遇到困难？", example: "例如：如果用户在完成一半任务时退出，了解原因很重要。" },
          { question: "您是否想发现隐藏的动机？", example: "例如：理解用户为何偏爱某一选项可以揭示潜在动机。" },
          { question: "您是否需要对原型进行详细反馈？", example: "例如：在产品发布前，了解用户对设计的详细意见至关重要。" },
          { question: "您是否想探索用户对设计的感受？", example: "例如：了解用户是否认为设计直观或令人沮丧。" },
          { question: "您是否需要倾听用户在执行任务时的想法？", example: "例如：了解用户在完成任务时的即时思维可以揭示出意想不到的问题。" },
        ],
        button: "测量与比较"
      },
      quantitative: {
        title: "测量与比较",
        questions: [
          { question: "您是否需要计算功能成功率？", example: "例如：测量有多少用户在没有错误的情况下完成任务。" },
          { question: "您是否想测试哪个设计更好？", example: "例如：比较两个页面版本，看看哪个转换率更高。" },
          { question: "您是否需要在某个尺度上测量用户满意度？", example: "例如：让用户在1到10的尺度上对其体验进行评分。" },
          { question: "您是否想比较不同群体的行为？", example: "例如：比较不同年龄段用户如何与您的应用程序互动。" },
          { question: "您是否需要测量用户成功的频率？", example: "例如：评估用户成功完成任务的次数与失败的次数。" },
        ],
        button: "查看结果"
      },
      result: {
        title: "最终结果",
        recommendation: "推荐的研究方法：",
        testsTitle: "测试建议",
        toolsTitle: "推荐工具",
        descriptions: {
          qualitative: "定性方法可以深入了解用户的体验和动机。",
          quantitative: "定量方法非常适合统计测量和分析数据。",
          mixed: "混合方法结合了定性和定量方法，为您提供完整的视角。"
        },
        tests: {
          qualitative: [
            { name: "深度访谈", detail: "通过开放式问题探讨用户的意见和感受。" },
            { name: "情境观察", detail: "在用户的环境中研究他们如何与产品互动。" },
            { name: "焦点小组", detail: "与小组讨论主题，以获取多种观点。" },
            { name: "用户日记", detail: "允许用户随时间记录他们使用产品的体验。" },
            { name: "同理心地图", detail: "帮助理解用户在互动时的思想和情感。" },
            { name: "人物角色", detail: "创建具有代表性的用户角色来指导设计。" },
            { name: "体验地图", detail: "可视化用户在使用产品不同阶段的旅程。" }
          ],
          quantitative: [
            { name: "调查问卷", detail: "从大量用户中收集结构化数据以进行统计分析。" },
            { name: "A/B测试", detail: "比较两个页面或功能的版本，看看哪个表现更好。" },
            { name: "树测试", detail: "评估导航结构，确保用户能够轻松找到信息。" },
            { name: "分析", detail: "通过页面停留时间或转化率等指标监控和分析用户行为。" },
            { name: "基准测试", detail: "将您的产品性能与竞争对手进行比较。" },
            { name: "启发式评估", detail: "通过专家评审识别可用性问题。" },
            { name: "卡诺模型", detail: "根据功能对用户满意度的影响对其进行分类。" }
          ],
          mixed: [
            { name: "案例研究", detail: "结合定性和定量数据分析现实世界中的情况。" },
            { name: "参与式设计研究", detail: "让用户参与设计过程，共同创造有效的解决方案。" },
            { name: "体验地图", detail: "结合观察和数据来可视化用户的产品旅程。" },
            { name: "卡片分类", detail: "帮助以符合用户逻辑的方式组织内容。" },
            { name: "焦点小组", detail: "与小组讨论主题，以获取多种观点。" },
            { name: "A/B测试", detail: "比较两个页面或功能的版本，看看哪个表现更好。" }
          ]
        },
        tools: "推荐工具：",
        toolsList: {
          qualitative: ["Lookback", "UserTesting", "Optimal Workshop", "Dovetail", "VWO", "Amplitude", "Datadog"],
          quantitative: ["Google Analytics", "Hotjar", "SurveyMonkey", "Mixpanel", "VWO", "Amplitude", "Datadog"],
          mixed: ["Maze", "Qualtrics", "UsabilityHub", "FullStory", "Google Analytics", "Hotjar", "SurveyMonkey", "Mixpanel", "VWO", "Amplitude", "Datadog"]
        },
        restart: "重新开始",
        note: "注意：我们与推荐的工具没有任何商业关系。",
        share: "您喜欢这个工具吗？快在社交媒体上分享吧！"
      },
      footer: {
        copyright: "©La Mala 2024",
        buyMeACoffee: "请我喝杯咖啡"
      }
    }
  };
  
  export default translations;
  